/**
 * Module: `export/export-markdown.ts` — linear markdown for Word (and PDF body).
 */

import { hardenSoftLineBreaks } from "@/lib/export/markdown-line-breaks";
import type {
  ResponseExportDocument,
  ResponseExportSource,
} from "@/lib/export/response-export-document";
import { SOURCE_DOCUMENT_SEPARATOR_MARKDOWN } from "@/lib/export/source-document-separator";
import { SOURCES_DOCUMENTAIRES_HEADING } from "@/lib/sources/source-categories";
import { capitalizeFirstLetter } from "@/lib/ui-helper";

function formatExportedAt(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat("fr-FR", {
    dateStyle: "long",
    timeStyle: "short",
  }).format(date);
}

function formatLinkedList(
  heading: string,
  items: Array<{ title: string; url: string }> | undefined
): string[] {
  if (!items?.length) return [];
  // Blank lines make the label its own paragraph in both converters.
  // Soft Markdown line breaks are rendered differently by pdfmake and md2docx.
  const lines = ["", capitalizeFirstLetter(`**${heading} :**`), ""];
  for (const item of items) {
    lines.push(`- [${capitalizeFirstLetter(item.title)}](${item.url})`);
  }
  return [...lines, ""];
}

/** Field label (`Résumé :`, `URL :`, …) — bold+underline in PDF/Word. */
function fieldLabel(label: string): string {
  const normalized = label.trim().replace(/\s*:\s*$/, "");
  return `**${normalized} :**`;
}

function formatSourceBlock(source: ResponseExportSource): string {
  // Wrap the link in bold (`**[title](url)**`) so PDF/Word keep bold on hyperlinks.
  const title = source.url
    ? `**[${source.title}](${source.url})**`
    : `**${source.title}**`;

  const lines: string[] = [title];

  const classTypeParts: string[] = [];
  if (source.classeDocument?.trim()) {
    classTypeParts.push(source.classeDocument.trim());
  }
  if (source.documentType?.trim()) {
    classTypeParts.push(source.documentType.trim());
  }
  if (classTypeParts.length > 0) {
    lines.push(capitalizeFirstLetter(classTypeParts.join(" | ")));
  }

  if (source.datePublication) {
    lines.push(
      capitalizeFirstLetter(
        `${fieldLabel("Publication")} ${source.datePublication}`
      )
    );
  }
  if (source.dateMiseAJour || source.date) {
    lines.push(
      capitalizeFirstLetter(
        `${fieldLabel("Mise à jour")} ${source.dateMiseAJour ?? source.date}`
      )
    );
  }

  if (source.summary) {
    lines.push(
      capitalizeFirstLetter(`${fieldLabel("Résumé")} ${source.summary}`)
    );
  }

  if (source.articleRefs?.length) {
    lines.push(
      capitalizeFirstLetter(fieldLabel("Référence(s) d'article(s) Légifrance"))
    );
    for (const ref of source.articleRefs) {
      const ancien = ref.ancien ? ` (anciennement ${ref.ancien})` : "";
      lines.push(capitalizeFirstLetter(`${ref.nouveau}${ancien}`));
    }
  }

  if (source.avertissementDocument) {
    lines.push(
      capitalizeFirstLetter(
        `${fieldLabel("Avertissement de lecture")} ${source.avertissementDocument}`
      )
    );
  }

  const linkLines = (source.links ?? [])
    .filter((link) => link.url.trim().length > 0)
    .map((link) =>
      capitalizeFirstLetter(
        `${fieldLabel(link.label)} [${link.url}](${link.url})`
      )
    );
  lines.push(...linkLines);

  lines.push(...formatLinkedList("Pièce(s) jointe(s)", source.piecesJointes));
  lines.push(...formatLinkedList("Outil(s) lié(s)", source.outilsLies));

  return lines.join("\n");
}

/**
 * Serializes an export document to a single markdown document.
 * Used as the Word pipeline input and as a shared textual layout.
 */
export function toExportMarkdown(doc: ResponseExportDocument): string {
  const parts: string[] = [];

  if (doc.disclaimer?.trim()) {
    parts.push(`**${doc.disclaimer.trim()}**`, "");
  }

  parts.push(
    `# ${doc.title}`,
    "",
    `_Exporté le ${formatExportedAt(doc.exportedAt)}_`,
    ""
  );

  if (doc.question.text.trim()) {
    parts.push("## Question", "", doc.question.text.trim(), "");
  }

  parts.push("## Réponse", "", doc.answerMarkdown.trim() || "_(vide)_", "");

  if (doc.sourceCategories.length > 0) {
    parts.push(`## ${SOURCES_DOCUMENTAIRES_HEADING}`, "");
    for (const category of doc.sourceCategories) {
      parts.push(`### ${category.label}`, "");
      for (const [sourceIndex, source] of category.sources.entries()) {
        if (sourceIndex > 0) {
          parts.push(SOURCE_DOCUMENT_SEPARATOR_MARKDOWN, "");
        }
        parts.push(formatSourceBlock(source), "");
      }
    }
  }

  const markdown =
    parts
      .join("\n")
      .replace(/\n{3,}/g, "\n\n")
      .trim() + "\n";

  return hardenSoftLineBreaks(markdown);
}
